import { useCallback, useEffect, useRef, useState } from 'react';
import { checkApiHealth, HEALTH_STATUS } from '../services/apiHealth';

const POLL_INTERVAL = 30000;

/**
 * Keeps a live view of the API health: polls the root endpoint, and re-checks when the
 * tab becomes visible again or the device regains connectivity.
 */
export const useApiHealth = ({ pollInterval = POLL_INTERVAL } = {}) => {
    const [health, setHealth] = useState({ status: HEALTH_STATUS.CHECKING });
    const [isChecking, setIsChecking] = useState(true);
    const isMounted = useRef(true);
    const inFlight = useRef(false);

    const recheck = useCallback(async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setIsChecking(true);

        const nextHealth = await checkApiHealth();

        inFlight.current = false;
        if (!isMounted.current) return;
        setHealth(nextHealth);
        setIsChecking(false);
    }, []);

    useEffect(() => {
        isMounted.current = true;
        recheck();

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') recheck();
        };

        const intervalId = setInterval(recheck, pollInterval);
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('online', recheck);
        window.addEventListener('offline', recheck);

        return () => {
            isMounted.current = false;
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('online', recheck);
            window.removeEventListener('offline', recheck);
        };
    }, [recheck, pollInterval]);

    return { health, isChecking, recheck };
};

export default useApiHealth;
